<template>
  <div class="segment-workspace">
    <div v-if="segmenting" class="segment-guard-sticky">
      <i class="el-icon-warning-outline segment-guard-sticky__icon"></i>
      <div class="segment-guard-sticky__body">
        <strong>切片进行中，请勿刷新或关闭页面</strong>
        <p>
          任务提交后将占用今日切片次数；刷新页面不会取消后台任务，但可能导致进度无法显示。
          请保持本页打开，直至下方进度条完成。
        </p>
      </div>
    </div>

    <div class="page-header">
      <h2>{{ platformName }}</h2>
      <p>
        上传长视频，按固定时长均分切片，预览并下载各段视频。
        <el-tag v-if="useMock" size="mini" type="warning">演示模式（Mock）</el-tag>
        <el-tag v-else-if="appEnv === 'local'" size="mini" type="info">本地环境</el-tag>
        <el-tag v-else-if="backendOnline" size="mini" type="success">Java 后端联调</el-tag>
        <el-tag v-else size="mini" type="danger">后端未连接</el-tag>
      </p>
    </div>

    <el-alert
      :title="uploadAlertTitle"
      type="info"
      :closable="false"
      show-icon
      class="tips-alert"
    />
    <el-alert
      v-if="!useMock"
      :title="retentionTip"
      type="warning"
      :closable="false"
      show-icon
      class="tips-alert"
    />

    <div class="uploader-wrap" :class="{ 'is-locked': segmenting, 'has-footage': !!currentFootage }">
      <el-upload
        class="uploader"
        action="#"
        :http-request="handleCustomUpload"
        :before-upload="beforeUpload"
        :show-file-list="false"
        :disabled="uploading || segmenting"
        drag
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将<em>视频</em>拖到此处，或点击上传</div>
        <div slot="tip" class="el-upload__tip">
          {{ uploadTipText }}
        </div>
      </el-upload>
      <div v-if="segmenting" class="uploader-lock-overlay">
        <i class="el-icon-loading"></i>
        <span>切片处理中，请勿刷新或关闭页面</span>
      </div>
    </div>

    <div v-if="uploading" class="progress-container">
      <el-progress
        class="progress-line-full"
        :percentage="uploadPercentage"
        :status="uploadPercentage === 100 ? 'success' : null"
      />
      <span class="progress-hint">正在上传视频…</span>
    </div>

    <div v-if="currentFootage" class="footage-panel">
      <h3>当前视频</h3>
      <el-descriptions :column="2" border size="small">
        <el-descriptions-item label="文件名">{{ currentFootage.name }}</el-descriptions-item>
        <el-descriptions-item label="项目 ID">{{ currentFootage.projectId }}</el-descriptions-item>
        <el-descriptions-item label="时长">{{ currentFootage.durationSeconds }} 秒</el-descriptions-item>
        <el-descriptions-item label="分辨率">
          {{ currentFootage.width || '-' }} × {{ currentFootage.height || '-' }}
        </el-descriptions-item>
      </el-descriptions>
      <video
        v-if="currentFootage.previewUrl"
        ref="sourcePreviewVideo"
        class="source-preview"
        :src="currentFootage.previewUrl"
        controls
        preload="metadata"
      />
    </div>

    <div v-if="currentFootage" class="segment-section">
      <h3 class="section-title">切片参数</h3>
      <segment-mode-selector
        :mode.sync="segmentMode"
        :segment-count.sync="segmentCount"
        :disabled="segmenting"
      />
      <segment-duration-selector
        v-if="segmentMode === 'FIXED_DURATION'"
        v-model="selectedDuration"
        :disabled="segmenting"
      />
      <segment-trim-input
        :head-trim-seconds.sync="headTrimSeconds"
        :tail-trim-seconds.sync="tailTrimSeconds"
        :max-duration-seconds="currentFootage.durationSeconds"
        :disabled="segmenting"
      />
      <segment-cover-panel
        :enabled.sync="coverEnabled"
        :title.sync="coverTitle"
        :episode-auto.sync="coverEpisodeAuto"
        :text-color.sync="coverTextColor"
        :text-stroke.sync="coverTextStroke"
        :text-stroke-color.sync="coverTextStrokeColor"
        :font-size.sync="coverFontSize"
        :preview-url="coverPreviewUrl"
        :project-id="currentFootage.projectId"
        :extracting="coverExtracting"
        :disabled="segmenting"
        @cover-file-change="onCoverFileChange"
        @extract-from-video="extractCoverFromVideoFrame"
      />
      <segment-watermark-panel
        :enabled.sync="watermarkEnabled"
        :text.sync="watermarkText"
        :text-color.sync="watermarkTextColor"
        :font-size.sync="watermarkFontSize"
        :position.sync="watermarkPosition"
        :text-stroke.sync="watermarkTextStroke"
        :text-stroke-color.sync="watermarkTextStrokeColor"
        :disabled="segmenting"
      />
      <segment-publish-panel
        :enabled.sync="publishEnabled"
        :hashtags.sync="publishHashtags"
        :description-template.sync="publishDescriptionTemplate"
        :disabled="segmenting"
      />
      <div class="segment-actions">
        <el-button
          type="primary"
          icon="el-icon-scissors"
          :loading="segmenting"
          :disabled="!canStartSegment"
          @click="handleSegment"
        >
          开始切片
        </el-button>
        <el-button v-if="segmentResults.length" :disabled="segmenting" @click="clearResults">
          清空结果
        </el-button>
        <el-button :disabled="uploading || segmenting" @click="resetWorkspace">
          重新上传
        </el-button>
      </div>
      <div v-if="segmenting" class="segment-progress-panel">
        <div class="segment-progress-panel__title">
          <i class="el-icon-warning"></i>
          <span>切片任务进行中</span>
        </div>
        <ul class="segment-progress-panel__tips">
          <li>请勿刷新、关闭或离开本页面</li>
          <li>刷新不会取消任务，但会占用切片次数且无法看到实时进度</li>
          <li>完成后将自动展示切片结果，请耐心等待</li>
        </ul>
        <p class="segment-progress-panel__hint">
          {{ segmentStatusHint || '正在后台处理，请耐心等待…' }}
        </p>
        <el-progress
          class="progress-line-full"
          :percentage="segmentProgress"
          :stroke-width="14"
        />
      </div>
    </div>

    <segment-result-list
      v-if="segmentResults.length"
      :clips="segmentResults"
      :playing-clip="playingClip"
      @play="openPlayer"
      @close-player="playingClip = null"
      @download-one="downloadSingle"
      @batch-download="downloadBatch"
      @selection-change="onSelectionChange"
      @remove-clip="removeExpiredClip"
    />
    <segment-publish-assistant
      v-if="segmentResults.length && publishEnabled"
      :task-id="segmentTaskId"
      :clips="segmentResults"
      :selected-clips="selectedClips"
      :cover-title="coverTitle"
      :publish-hashtags.sync="publishHashtags"
      :publish-description-template.sync="publishDescriptionTemplate"
      :use-mock="useMock"
      :fallback-file-name="publishPackFileName"
    />
  </div>
</template>

<script>
import SegmentDurationSelector from './SegmentDurationSelector.vue';
import SegmentModeSelector from './SegmentModeSelector.vue';
import SegmentTrimInput from './SegmentTrimInput.vue';
import SegmentCoverPanel from './SegmentCoverPanel.vue';
import SegmentWatermarkPanel from './SegmentWatermarkPanel.vue';
import SegmentPublishPanel from './SegmentPublishPanel.vue';
import SegmentPublishAssistant from './SegmentPublishAssistant.vue';
import SegmentResultList from './SegmentResultList.vue';
import { uploadVideoFile } from '../api/upload';
import { submitSegmentTask, resumeSegmentTask } from '../api/segment';
import { uploadCoverTemplate, extractCoverFromVideo } from '../api/cover';
import { fetchMyQuota } from '../api/auth';
import { request } from '../api/client';
import {
  API_PATHS,
  APP_ENV,
  PLATFORM_NAME,
  USE_MOCK,
  VIDEO_RETENTION_HOURS,
} from '../config';
import { formatFileSizeMb } from '../utils/formatQuota';
import { DEFAULT_SEGMENT_DURATION } from '../constants/segmentDurations';
import { SEGMENT_MODES } from '../constants/segmentModes';
import { WATERMARK_POSITIONS } from '../constants/watermarkPositions';
import {
  COVER_FONT_SIZE_DEFAULT,
  WATERMARK_FONT_SIZE_DEFAULT,
} from '../constants/textFontSizes';
import { validateVideoFile } from '../utils/videoValidate';
import { downloadFile, downloadFilesBatch, downloadSegmentBatchZip } from '../utils/download';
import { loadWorkspaceState, saveWorkspaceState, clearWorkspaceState } from '../utils/workspaceStorage';

export default {
  name: 'SegmentWorkspace',
  components: {
    SegmentDurationSelector,
    SegmentModeSelector,
    SegmentTrimInput,
    SegmentCoverPanel,
    SegmentWatermarkPanel,
    SegmentPublishPanel,
    SegmentPublishAssistant,
    SegmentResultList,
  },
  data() {
    return {
      platformName: PLATFORM_NAME,
      appEnv: APP_ENV,
      useMock: USE_MOCK,
      quotaLimits: null,
      backendOnline: false,
      uploading: false,
      uploadPercentage: 0,
      segmenting: false,
      segmentProgress: 0,
      segmentStatusHint: '',
      segmentMode: SEGMENT_MODES.FIXED_DURATION,
      segmentCount: 10,
      headTrimSeconds: 0,
      tailTrimSeconds: 0,
      selectedDuration: DEFAULT_SEGMENT_DURATION,
      coverEnabled: false,
      coverTitle: '',
      coverEpisodeAuto: true,
      coverTextColor: '#FFFFFF',
      coverTextStroke: true,
      coverTextStrokeColor: '#000000',
      coverFontSize: COVER_FONT_SIZE_DEFAULT,
      coverFile: null,
      coverPreviewUrl: '',
      coverTemplateObjectKey: null,
      coverExtracting: false,
      watermarkEnabled: false,
      watermarkText: '',
      watermarkTextColor: '#FFFFFF',
      watermarkFontSize: WATERMARK_FONT_SIZE_DEFAULT,
      watermarkPosition: WATERMARK_POSITIONS.BOTTOM_RIGHT,
      watermarkTextStroke: true,
      watermarkTextStrokeColor: '#000000',
      publishEnabled: false,
      publishHashtags: '',
      publishDescriptionTemplate: '',
      selectedClips: [],
      currentFootage: null,
      segmentResults: [],
      segmentTaskId: null,
      playingClip: null,
    };
  },
  computed: {
    canStartSegment() {
      if (!this.currentFootage || this.uploading || this.segmenting) {
        return false;
      }
      if (this.coverEnabled && !this.coverFile && !this.coverTemplateObjectKey) {
        return false;
      }
      if (this.watermarkEnabled && !this.watermarkText.trim()) {
        return false;
      }
      return true;
    },
    retentionTip() {
      return `服务器上的原视频与切片文件约保留 ${VIDEO_RETENTION_HOURS} 小时，到期后自动清理。请及时下载需要的切片。`;
    },
    uploadAlertTitle() {
      if (!this.quotaLimits) {
        return '支持 MP4 / MKV / MOV 等，上传限制按当前账号档位';
      }
      const size = formatFileSizeMb(this.quotaLimits.maxFileMb);
      return `支持 MP4 / MKV / MOV 等，当前档位单文件最大 ${size}，时长 ${this.quotaLimits.maxDurationMin} 分钟`;
    },
    uploadTipText() {
      if (!this.quotaLimits) {
        return '单文件与时长上限按当前账号档位限制';
      }
      const size = formatFileSizeMb(this.quotaLimits.maxFileMb);
      return `单文件不超过 ${size}，视频时长不超过 ${this.quotaLimits.maxDurationMin} 分钟`;
    },
    publishPackFileName() {
      const baseName = this.currentFootage && this.currentFootage.name
        ? this.currentFootage.name.replace(/\.[^.]+$/, '')
        : 'publish';
      const count = this.selectedClips.length;
      if (count > 0 && count < this.segmentResults.length) {
        return `${baseName}_publish_pack_${count}.zip`;
      }
      return `${baseName}_publish_pack.zip`;
    },
  },
  mounted() {
    this.checkBackendHealth();
    this.loadUserQuota();
    this.restoreWorkspaceState();
  },
  watch: {
    segmenting() {
      this.syncPageLeaveGuard();
    },
    uploading() {
      this.syncPageLeaveGuard();
    },
  },
  beforeDestroy() {
    this.revokePreviewUrl();
    this.removePageLeaveGuard();
  },
  methods: {
    syncPageLeaveGuard() {
      if (this.segmenting || this.uploading) {
        window.addEventListener('beforeunload', this.onPageLeaveWarning);
      } else {
        this.removePageLeaveGuard();
      }
    },
    removePageLeaveGuard() {
      window.removeEventListener('beforeunload', this.onPageLeaveWarning);
    },
    onPageLeaveWarning(event) {
      event.preventDefault();
      event.returnValue = '';
    },
    async loadUserQuota() {
      if (USE_MOCK) return;
      try {
        const data = await fetchMyQuota();
        this.quotaLimits = data.limits;
      } catch (e) {
        // 未登录或接口不可用时保持通用提示
      }
    },
    async checkBackendHealth() {
      if (USE_MOCK) {
        this.backendOnline = false;
        return;
      }
      try {
        await request(API_PATHS.health);
        this.backendOnline = true;
      } catch (e) {
        this.backendOnline = false;
      }
    },
    beforeUpload(file) {
      const result = validateVideoFile(file, {
        maxFileMb: this.quotaLimits && this.quotaLimits.maxFileMb,
      });
      if (!result.ok) {
        this.$message.error(result.message);
        return false;
      }
      return true;
    },
    async handleCustomUpload({ file }) {
      this.uploading = true;
      this.uploadPercentage = 0;
      this.segmentResults = [];
      this.segmentTaskId = null;
      try {
        const data = await uploadVideoFile(file, (evt) => {
          this.uploadPercentage = Math.round(evt.percent || 0);
        });
        this.setCurrentFootage(file, data);
        saveWorkspaceState({ projectId: data.projectId, taskId: null });
        this.$message.success('上传完成');
      } catch (e) {
        this.$message.error(e.message || '上传失败');
      } finally {
        this.uploading = false;
      }
    },
    setCurrentFootage(file, data) {
      this.revokePreviewUrl();
      const previewUrl = data.url || URL.createObjectURL(file);
      this.currentFootage = {
        name: data.fileName || file.name,
        projectId: data.projectId,
        videoId: data.videoId,
        durationSeconds: data.durationSeconds,
        width: data.width,
        height: data.height,
        previewUrl,
      };
    },
    revokePreviewUrl() {
      if (this.currentFootage && this.currentFootage.previewUrl
        && this.currentFootage.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.currentFootage.previewUrl);
      }
    },
    async handleSegment() {
      if (!this.currentFootage) return;
      if (this.coverEnabled && !this.coverFile && !this.coverTemplateObjectKey) {
        this.$message.warning('请先上传封面底图');
        return;
      }
      if (this.watermarkEnabled && !this.watermarkText.trim()) {
        this.$message.warning('请填写水印文字');
        return;
      }
      this.segmenting = true;
      this.segmentProgress = 0;
      this.segmentStatusHint = '提交任务…';
      this.$message({
        type: 'warning',
        message: '切片已开始，请勿刷新或关闭页面',
        duration: 5000,
        showClose: true,
      });
      try {
        let templateObjectKey = this.coverTemplateObjectKey;
        if (this.coverEnabled && this.coverFile) {
          this.segmentStatusHint = '上传封面底图…';
          const uploaded = await uploadCoverTemplate(
            this.currentFootage.projectId,
            this.coverFile,
          );
          templateObjectKey = uploaded.objectKey;
          this.coverTemplateObjectKey = templateObjectKey;
        }
        const result = await submitSegmentTask(
          this.currentFootage.projectId,
          {
            mode: this.segmentMode,
            durationSeconds: this.selectedDuration,
            segmentCount: this.segmentCount,
            headTrimSeconds: this.headTrimSeconds,
            tailTrimSeconds: this.tailTrimSeconds,
            cover: this.coverEnabled
              ? {
                  enabled: true,
                  templateObjectKey,
                  title: this.coverTitle,
                  episodeAuto: this.coverEpisodeAuto,
                  textColor: this.coverTextColor,
                  textStroke: this.coverTextStroke,
                  textStrokeColor: this.coverTextStrokeColor,
                  fontSize: this.coverFontSize,
                }
              : null,
            watermark: this.watermarkEnabled
              ? {
                  enabled: true,
                  text: this.watermarkText.trim(),
                  textColor: this.watermarkTextColor,
                  fontSize: this.watermarkFontSize,
                  position: this.watermarkPosition,
                  textStroke: this.watermarkTextStroke,
                  textStrokeColor: this.watermarkTextStrokeColor,
                }
              : null,
            publish: this.publishEnabled
              ? {
                  enabled: true,
                  hashtags: this.publishHashtags,
                  descriptionTemplate: this.publishDescriptionTemplate,
                }
              : null,
          },
          {
            onProgress: (evt) => {
              this.segmentProgress = evt.progressPercent || 0;
              this.segmentStatusHint = evt.progressMessage || evt.status;
            },
          },
        );
        this.applyTaskResult(result);
        this.$message.success(`切片完成，共 ${this.segmentResults.length} 段`);
        this.$emit('quota-refresh');
      } catch (e) {
        this.$message.error(e.message || '切片失败');
      } finally {
        this.segmenting = false;
      }
    },
    applyTaskResult(result) {
      this.segmentResults = result.segments || [];
      this.segmentTaskId = result.taskId || null;
      this.publishHashtags = result.publishHashtags || this.publishHashtags;
      this.publishDescriptionTemplate = result.publishDescriptionTemplate || this.publishDescriptionTemplate;
      if (result.publishHashtags || result.publishDescriptionTemplate) {
        this.publishEnabled = true;
      }
      if (result.coverTitle) {
        this.coverTitle = result.coverTitle;
      }
      this.selectedClips = [];
      saveWorkspaceState({
        projectId: this.currentFootage && this.currentFootage.projectId,
        taskId: this.segmentTaskId,
      });
    },
    onSelectionChange(clips) {
      this.selectedClips = clips || [];
    },
    removeExpiredClip(clip) {
      const key = clip.id != null ? clip.id : clip.index;
      this.segmentResults = this.segmentResults.filter(
        item => (item.id != null ? item.id : item.index) !== key,
      );
      if (this.playingClip && (this.playingClip.id != null ? this.playingClip.id : this.playingClip.index) === key) {
        this.playingClip = null;
      }
      this.selectedClips = this.selectedClips.filter(
        item => (item.id != null ? item.id : item.index) !== key,
      );
    },
    async resumeRunningTask(taskId) {
      this.segmenting = true;
      try {
        const result = await resumeSegmentTask(taskId, {
          onProgress: (evt) => {
            this.segmentProgress = evt.progressPercent || 0;
            this.segmentStatusHint = evt.progressMessage || evt.status;
          },
        });
        this.applyTaskResult(result);
      } catch (e) {
        this.$message.error(e.message || '恢复任务失败');
      } finally {
        this.segmenting = false;
      }
    },
    async restoreWorkspaceState() {
      if (USE_MOCK) return;
      const saved = loadWorkspaceState();
      if (saved.taskId) {
        try {
          await this.resumeRunningTask(saved.taskId);
        } catch (e) {
          // ignore restore failure
        }
      }
    },
    openPlayer(clip) {
      this.playingClip = clip;
    },
    async downloadSingle(clip) {
      if (!clip.url) {
        this.$message.warning('Mock 模式下无真实下载地址');
        return;
      }
      try {
        await downloadFile(clip.url, clip.fileName);
        if (clip.coverEnabled && clip.thumbnailUrl) {
          const coverName = clip.fileName.replace(/\.[^.]+$/, '') + '_cover.jpg';
          await downloadFile(clip.thumbnailUrl, coverName);
        }
      } catch (e) {
        this.$message.error(e.message || '下载失败');
      }
    },
    onCoverFileChange({ file, previewUrl }) {
      if (this.coverPreviewUrl && this.coverPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.coverPreviewUrl);
      }
      this.coverFile = file;
      this.coverPreviewUrl = previewUrl;
      this.coverTemplateObjectKey = null;
    },
    async extractCoverFromVideoFrame() {
      if (!this.currentFootage || !this.currentFootage.projectId) {
        this.$message.warning('请先上传视频');
        return;
      }
      this.coverExtracting = true;
      try {
        const uploaded = await extractCoverFromVideo(this.currentFootage.projectId, 1);
        this.coverTemplateObjectKey = uploaded.objectKey;
        this.coverFile = null;
        if (this.coverPreviewUrl && this.coverPreviewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.coverPreviewUrl);
        }
        this.coverPreviewUrl = uploaded.url || '';
        this.$message.success('已从视频截取封面底图');
      } catch (e) {
        this.$message.error(e.message || '截取封面底图失败');
      } finally {
        this.coverExtracting = false;
      }
    },
    revokeCoverPreviewUrl() {
      if (this.coverPreviewUrl && this.coverPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.coverPreviewUrl);
      }
    },
    async downloadBatch(clips) {
      if (!clips.length) {
        this.$message.warning('暂无可下载文件');
        return;
      }
      const loading = this.$loading({
        lock: true,
        text: '正在准备下载，请稍候…',
        customClass: 'segment-download-loading',
        background: 'rgba(0, 0, 0, 0.55)',
      });
      try {
        if (this.useMock || !this.segmentTaskId) {
          const items = clips.filter(c => c.url).map(c => ({ url: c.url, fileName: c.fileName }));
          if (!items.length) {
            this.$message.warning('Mock 模式下无真实下载地址');
            return;
          }
          loading.setText(`正在下载 ${items.length} 个文件，请稍候…`);
          const result = await downloadFilesBatch(items);
          this.$message.success(`已开始下载 ${result.count} 个文件`);
          return;
        }
        loading.setText(`正在打包 ${clips.length} 个切片，请稍候…`);
        const segmentIds = clips.map(c => c.id).filter(Boolean);
        const baseName = this.currentFootage && this.currentFootage.name
          ? this.currentFootage.name.replace(/\.[^.]+$/, '')
          : 'segments';
        const fallbackFileName = segmentIds.length && segmentIds.length < this.segmentResults.length
          ? `${baseName}_segments_${segmentIds.length}.zip`
          : `${baseName}_segments.zip`;
        await downloadSegmentBatchZip(this.segmentTaskId, segmentIds, fallbackFileName);
        this.$message.success(`已开始下载压缩包（${clips.length} 个切片）`);
      } catch (e) {
        this.$message.error(e.message || '批量下载失败');
      } finally {
        loading.close();
      }
    },
    clearResults() {
      this.segmentResults = [];
      this.segmentTaskId = null;
      this.playingClip = null;
      this.selectedClips = [];
      saveWorkspaceState({ taskId: null });
    },
    resetWorkspace() {
      this.revokePreviewUrl();
      this.revokeCoverPreviewUrl();
      this.currentFootage = null;
      this.segmentResults = [];
      this.segmentTaskId = null;
      this.playingClip = null;
      this.uploadPercentage = 0;
      this.headTrimSeconds = 0;
      this.tailTrimSeconds = 0;
      this.coverEnabled = false;
      this.coverTitle = '';
      this.coverEpisodeAuto = true;
      this.coverTextColor = '#FFFFFF';
      this.coverTextStroke = true;
      this.coverTextStrokeColor = '#000000';
      this.coverFontSize = COVER_FONT_SIZE_DEFAULT;
      this.coverFile = null;
      this.coverPreviewUrl = '';
      this.coverTemplateObjectKey = null;
      this.coverExtracting = false;
      this.watermarkEnabled = false;
      this.watermarkText = '';
      this.watermarkTextColor = '#FFFFFF';
      this.watermarkFontSize = WATERMARK_FONT_SIZE_DEFAULT;
      this.watermarkPosition = WATERMARK_POSITIONS.BOTTOM_RIGHT;
      this.watermarkTextStroke = true;
      this.watermarkTextStrokeColor = '#000000';
      this.publishEnabled = false;
      this.publishHashtags = '';
      this.publishDescriptionTemplate = '';
      this.selectedClips = [];
      clearWorkspaceState();
    },
  },
};
</script>

<style scoped>
.segment-workspace {
  background: rgba(255, 255, 255, 0.96);
  border-radius: 12px;
  padding: 24px;
  color: #111827;
}
.segment-guard-sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: -8px 0 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.08);
}
.segment-guard-sticky__icon {
  font-size: 22px;
  color: #dc2626;
  margin-top: 2px;
}
.segment-guard-sticky__body strong {
  display: block;
  color: #b91c1c;
  font-size: 15px;
  margin-bottom: 4px;
}
.segment-guard-sticky__body p {
  margin: 0;
  color: #991b1b;
  font-size: 13px;
  line-height: 1.5;
}
.page-header h2 {
  margin: 0 0 8px;
}
.page-header p {
  margin: 0;
  color: #6b7280;
}
.tips-alert {
  margin: 16px 0;
}
.uploader-wrap {
  position: relative;
  width: 100%;
}
.uploader {
  width: 100%;
}
.uploader-wrap >>> .el-upload {
  width: 100%;
  display: block;
}
.uploader-wrap >>> .el-upload-dragger {
  width: 100%;
  height: 250px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.uploader-wrap.is-locked {
  opacity: 0.85;
}
.uploader-lock-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  z-index: 2;
}
.progress-container {
  margin-top: 16px;
  width: 100%;
}
.progress-line-full {
  display: flex;
  align-items: center;
  width: 100%;
}
.progress-line-full >>> .el-progress-bar {
  flex: 1 1 0;
  min-width: 0;
  width: 0;
  padding-right: 0;
  margin-right: 12px;
  display: block;
}
.progress-line-full >>> .el-progress-bar__outer {
  width: 100%;
}
.progress-line-full >>> .el-progress__text {
  flex-shrink: 0;
  margin-left: 0;
  line-height: 1;
  white-space: nowrap;
}
.progress-line-full >>> .el-progress.is-success .el-progress__text {
  display: inline-flex;
  align-items: center;
}
.progress-hint {
  display: block;
  margin-top: 8px;
  color: #6b7280;
  font-size: 13px;
}
.footage-panel,
.segment-section {
  margin-top: 24px;
}
.source-preview {
  width: 100%;
  max-height: 360px;
  margin-top: 12px;
  background: #000;
}
.section-title {
  margin: 0 0 8px;
}
.segment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}
.segment-progress-panel {
  margin-top: 16px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  box-sizing: border-box;
}
.segment-progress-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #d97706;
  font-weight: 600;
  margin-bottom: 8px;
}
.segment-progress-panel__tips {
  margin: 0 0 10px 18px;
  padding: 0;
  color: #92400e;
  font-size: 13px;
  line-height: 1.6;
}
.segment-progress-panel__hint {
  margin: 0 0 12px;
  color: #92400e;
  font-size: 13px;
}
</style>

<style>
.segment-download-loading .el-loading-spinner {
  margin-top: -28px;
}
.segment-download-loading .el-loading-text {
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
}
</style>
